// 本地风险巡检（TS 镜像 keytail-core/src/risk.rs）。只对 metadata 计算，绝不解密。
// 文案口径"整理建议"、禁红警（设计语言 §3.4/§6），与 Rust 逐字一致。

import type { AccountItem, SecretItem, RiskItem, PasswordStrength } from './model'

const DAY_MS = 86_400_000

/** chrono Duration::num_days 语义：整天数，朝零截断。 */
function numDays(fromIso: string, toDate: Date): number {
  return Math.trunc((toDate.getTime() - new Date(fromIso).getTime()) / DAY_MS)
}

function riskId(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return 'risk_' + Math.random().toString(36).slice(2)
  }
}

export function detectAccountRisks(account: AccountItem, now: Date = new Date()): RiskItem[] {
  const out: RiskItem[] = []
  const nowIso = now.toISOString()

  // 长期未更新：上次改密 > 365 天
  if (account.last_password_changed_at) {
    const days = numDays(account.last_password_changed_at, now)
    if (days > 365) {
      out.push({
        id: riskId(),
        target_type: 'account',
        target_id: account.id,
        risk_type: 'stale_password',
        level: 'medium',
        message: `这把钥匙已经 ${days} 天没整理了`,
        ignored: false,
        created_at: nowIso,
      })
    }
  }

  // 未开启 2FA
  if (!account.has_2fa) {
    out.push({
      id: riskId(),
      target_type: 'account',
      target_id: account.id,
      risk_type: 'no_2fa',
      level: 'medium',
      message: '建议为这个账号开启二次验证',
      ignored: false,
      created_at: nowIso,
    })
  }

  return out
}

export function detectSecretRisks(secret: SecretItem, now: Date = new Date()): RiskItem[] {
  const out: RiskItem[] = []
  const nowIso = now.toISOString()

  if (secret.status === 'rotate_due') {
    out.push({
      id: riskId(),
      target_type: 'secret',
      target_id: secret.id,
      risk_type: 'rotation_due',
      level: 'high',
      message: '这把密钥到了建议轮换的日子',
      ignored: false,
      created_at: nowIso,
    })
  }

  if (secret.next_rotation_at) {
    const days = numDays(secret.next_rotation_at, now) // (next - now) 反向：用 now - next 再取负
    // Rust: days = (next - now).num_days(); if days < 0 → overdue, 文案用 -days。
    // numDays(next, now) = (now - next) days；overdue 时 now>next → 正数 = -days。
    if (days > 0) {
      out.push({
        id: riskId(),
        target_type: 'secret',
        target_id: secret.id,
        risk_type: 'rotation_overdue',
        level: 'high',
        message: `已超出建议轮换日期 ${days} 天`,
        ignored: false,
        created_at: nowIso,
      })
    }
  }

  return out
}

/** 估算密码强度（score 0-4 → weak/medium/strong），镜像 risk::classify_strength。 */
export function classifyStrength(score: number): PasswordStrength {
  if (score <= 1) return 'weak'
  if (score === 2) return 'medium'
  return 'strong'
}
